/**
 * Keep a `<select>`'s live value on the option the render actually chose
 * (NEWS-238).
 *
 * ## The bug
 *
 * A `<select>` has two notions of what is chosen. The `selected` **content
 * attribute** is what a template writes, and the option's **selectedness
 * property** is what the user sees and what `el.value` reports. Per HTML, once
 * an option's selectedness has been set by the user or by script, its dirtiness
 * flag is set and *changes to the content attribute no longer move the
 * selection*.
 *
 * kerf handles this: when a morph changes a `selected` attribute it also sets
 * the property, so the dirty flag is bypassed. **But it can only do that when
 * the attribute changes.** If the rendered attribute is already where it should
 * be, there is nothing to diff, no property write, and a value the user has
 * moved stays moved — permanently, because every later render agrees with the
 * DOM and changes nothing.
 *
 * That is not hypothetical. Captured from a CI failure, on one element:
 *
 *     "action": "hp-interval", "value": "10800000", "attrOn": "3600000"
 *
 * The attribute says 1 hour, the control shows 3. It reached that state because
 * the user picked 3 hours while the server's answer — after a clamp — was the
 * 1 hour already rendered. Nothing changed, so nothing was synced.
 *
 * For a user: pick a `<select>` value the server then normalises to the value
 * already rendered, and the dropdown keeps showing the old value even though the
 * stored setting is right — the UI lies about it until something else happens to
 * move that attribute. (The original case was a since-removed cadence dropdown;
 * the sync is generic and still guards every `<select>` the app renders.)
 *
 * ## The fix
 *
 * After every render, make the attribute authoritative — which it already is
 * conceptually, since it is what the render decided. The property is the copy
 * that drifts.
 *
 * Deliberately driven by the attribute rather than by app state: it needs no
 * per-control wiring, it cannot disagree with what was just rendered, and it
 * covers every `<select>` the app has or gains. And it is inert whenever the two
 * already agree, which is almost always.
 */

/** One `<select>`'s two notions of what is chosen, as far as the rule cares. */
export interface SelectState {
  /** What `el.value` reports — the property, and what the user sees. */
  value: string;
  /** The value of the option carrying the `selected` attribute, if any. */
  attrValue: string | null;
}

/**
 * The value this select should be set to, or `null` to leave it alone.
 *
 * Split out from the DOM so the rule is unit-testable — the whole failure was
 * invisible for weeks precisely because it only appeared in a browser under an
 * interleaving no test could schedule.
 */
export function selectValueToApply(state: SelectState): string | null {
  // No option carries the attribute: the render expressed no opinion (a control
  // whose options are all unselected), so there is nothing to enforce. Writing
  // a value here would invent a choice the app never made.
  if (state.attrValue === null) return null;
  return state.attrValue === state.value ? null : state.attrValue;
}

/**
 * Apply the rule to every `<select>` under `root`.
 *
 * Returns how many it corrected, which is what makes "this did something" an
 * assertion rather than an assumption.
 */
export function syncSelects(root: ParentNode): number {
  let fixed = 0;
  for (const el of root.querySelectorAll('select')) {
    const attrOption = [...el.options].find((o) => o.hasAttribute('selected'));
    const next = selectValueToApply({ value: el.value, attrValue: attrOption?.value ?? null });
    if (next !== null) {
      el.value = next;
      fixed++;
    }
  }
  return fixed;
}

/**
 * The same rule for a radio group (NM-25).
 *
 * A radio's `checked` **property** drifts from its `checked` **content
 * attribute** for exactly the reason a `<select>`'s does: once the user (or
 * script) moves the selection, the input's dirtiness flag is set and a later
 * change to the content attribute no longer moves it. kerf re-syncs the
 * property only when it *diffs* the attribute, so a morph that re-renders the
 * same rendered choice leaves a user-moved radio moved.
 *
 * The theme tile picker is a radio group whose selection is server state, so a
 * poll landing mid-PATCH is precisely the interleaving that would strand it.
 * This is the radio twin of {@link syncSelects}: after each render, make the
 * rendered `checked` attribute authoritative over the property.
 *
 * Grouped as a pure function so the decision is unit-testable without a DOM —
 * the whole class of bug is invisible except under an interleaving no test can
 * schedule in a browser.
 */
export interface RadioState {
  /** What `el.checked` reports — the property, and what the user sees. */
  checked: boolean;
  /** Whether this radio carries the `checked` content attribute. */
  attrChecked: boolean;
}

/**
 * Given one radio group's rendered state, which member's property must change,
 * as index → the value it should be set to. Returns an empty array when the
 * render expressed no opinion (no member carries the attribute) or the property
 * already agrees with the attribute everywhere.
 */
export function radiosToApply(group: readonly RadioState[]): { index: number; checked: boolean }[] {
  // No member carries the attribute: the render chose nothing (a group rendered
  // before any selection exists), so enforcing "unchecked" would invent one.
  if (!group.some((r) => r.attrChecked)) return [];
  const fixes: { index: number; checked: boolean }[] = [];
  group.forEach((r, index) => {
    if (r.checked !== r.attrChecked) fixes.push({ index, checked: r.attrChecked });
  });
  return fixes;
}

/**
 * Apply the radio rule to every `name`-grouped radio under `root`. Returns how
 * many inputs it corrected.
 */
export function syncRadios(root: ParentNode): number {
  const groups = new Map<string, HTMLInputElement[]>();
  for (const el of root.querySelectorAll('input[type=radio]')) {
    if (!(el instanceof HTMLInputElement)) continue;
    const members = groups.get(el.name);
    if (members) members.push(el);
    else groups.set(el.name, [el]);
  }

  let fixed = 0;
  for (const members of groups.values()) {
    const state = members.map((r) => ({ checked: r.checked, attrChecked: r.hasAttribute('checked') }));
    for (const fix of radiosToApply(state)) {
      const member = members.at(fix.index);
      if (member === undefined) throw new Error(`radio fix index ${String(fix.index)} is outside its group`);
      member.checked = fix.checked;
      fixed++;
    }
  }
  return fixed;
}
